import React, { useEffect, useState } from "react";
import {
    Paper,
    Skeleton,
    Typography,
    Box,
    Chip,
    Divider,
    Button,
    Grid,
    Link,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useAuditsController } from "./context/audits-controller";
import { useAuditsState } from "./context/audits-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";

export function AuditDetailComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const { loadDetail } = useAuditsController();
    const { auditDetail } = useAuditsState();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                await loadDetail(Number(id));
                setInitialLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handleBack = () => {
        navigate('/auditoria');
    };

    const getStatusColor = (status: number) => {
        switch (status) {
            case 1:
                return 'success';
            case 2:
                return 'warning';
            case 3:
                return 'info';
            default:
                return 'default';
        }
    };

    return (
        <Box>
            <PageHeader
                title={auditDetail?.processNumber || 'Carregando...'}
                subtitle=""
                breadcrumbs={[
                    { label: 'Auditoria', href: '/auditoria' },
                    { label: auditDetail?.processNumber || 'Carregando...' }
                ]}
            />

            {initialLoading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            ) : (
                <React.Fragment>
                    <Box sx={{ mb: 3 }}>
                        <Chip
                            label={auditDetail?.statusDescription || 'Não especificado'}
                            color={getStatusColor(auditDetail?.status || 0)}
                            size="medium"
                        />
                    </Box>

                    <Paper sx={{ p: 4, mb: 3 }} elevation={2}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            Informações da Auditoria
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <DescriptionIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Número do Processo
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {auditDetail?.processNumber || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <BusinessIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Unidade Gestora
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {auditDetail?.unitName || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <CalendarTodayIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Ano de Referência
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {auditDetail?.year || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <DescriptionIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Situação
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {auditDetail?.statusDescription || 'Não especificado'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {auditDetail?.regulatoryAgencyDescription && (
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <BusinessIcon color="primary" />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Órgão Regulamentador
                                            </Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {auditDetail.regulatoryAgencyDescription}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}

                            {auditDetail?.description && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Descrição
                                    </Typography>
                                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                        {auditDetail.description}
                                    </Typography>
                                </Grid>
                            )}

                            {auditDetail?.url && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Documento:
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            endIcon={<OpenInNewIcon />}
                                            component={Link}
                                            href={auditDetail.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ textDecoration: 'none' }}
                                        >
                                            Abrir Documento
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            size="large"
                        >
                            Voltar para Auditoria
                        </Button>
                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
}
